// Internationalization for SmartGTD WebApp
import React from 'react';

const translations = {
    en: {
        // Navigation
        dashboard: "Dashboard",
        tasks: "Tasks",
        reminders: "Reminders",
        calendar: "Calendar",
        conversations: "Chat",
        chat: "Chat",
        history: "History",
        config: "Config",
        logout: "Logout",
        search: "Search",
        searchTasks: "Search tasks...",
        
        // Dashboard
        welcomeBack: "Welcome back!",
        productivityOverview: "Here's your productivity overview",
        activeTasks: "Active Tasks",
        quickActions: "Quick Actions",
        recentActivity: "Recent Activity",
        viewAll: "View All",
        viewConversations: "View Conversations",
        recentConversationsText: "Your recent conversations will appear here",
        
        // Quick Actions
        manageTasks: "Manage your GTD tasks",
        viewReminders: "View upcoming reminders",
        checkSchedule: "Check your schedule",
        chatHistory: "Chat history with AI",
        integrations: "Integrations",
        integrationsDesc: "Configure integrations with Google Calendar and others",
        
        // Role-based access
        accessRestricted: "Access Restricted",
        adminRoleRequired: "Admin role required to modify system prompt",
        currentRole: "Current role",
        modelSelectionRestricted: "Model selection requires admin or power user role.",
        accessDeniedAdmin: "Access denied. Admin role required.",
        accessDeniedPowerUser: "Access denied. Admin or power user role required.",
        modelUpdated: "Model updated successfully",
        failedToUpdateModel: "Failed to update model",
        
        // Login
        redirectingLogin: "Redirecting to secure login...",
        
        // Tasks
        noTasks: "No tasks found",
        createTask: "Create Task",
        taskTitle: "Task Title",
        taskDescription: "Description",
        priority: "Priority",
        status: "Status",
        dueDate: "Due Date",
        yourTasks: "Your Tasks",
        filters: "Filters",
        all: "All",
        nextAction: "Next Action",
        scheduled: "Scheduled",
        waitingFor: "Waiting For",
        somedayMaybe: "Someday/Maybe",
        active: "Active",
        completed: "Completed",
        due: "Due",
        category: "Category",
        tryAdjustingFilters: "Try adjusting your filters",
        startAddingTasks: "Start by adding tasks through the Telegram or WhatsApp bot",
        
        // GTD Workflow
        gtdWorkflow: "GTD Workflow",
        chooseFocusArea: "Choose your focus area",
        nextActions: "Next Actions",
        doNow: "Do Now",
        doLater: "Do Later",
        delegated: "Delegated",
        someday: "Someday",
        maybeLater: "Maybe Later",
        nextActionsTasks: "Next Actions",
        scheduledTasks: "Scheduled Tasks",
        waitingForTasks: "Waiting For",
        somedayMaybeTasks: "Someday/Maybe",
        
        // Reminders
        noReminders: "No reminders found",
        upcomingReminders: "Upcoming Reminders",
        scheduledFor: "Scheduled for",
        scheduledReminders: "Scheduled Reminders",
        next24h: "Next 24h",
        next24Hours: "Next 24 Hours",
        overdue: "Overdue",
        overdueReminders: "Overdue Reminders",
        cancelledReminders: "Cancelled Reminders",
        allReminders: "All Reminders",
        createRemindersBot: "Create reminders through the Telegram or WhatsApp bot",
        noRemindersFilter: "No {filter} reminders at the moment",
        created: "Created",
        upcoming: "Upcoming",
        
        // Recurring Reminders
        routines: "Routines",
        noRoutines: "No routines yet",
        noRoutinesDesc: "Ask the bot to create recurring reminders like:",
        noRoutinesExample: "\"Remind me to take medication every day at 8h, 14h, 21h\"",
        everyMinute: "Every minute",
        everyNMinutes: "Every {n} minutes",
        everyHour: "Every hour",
        everyNHours: "Every {n} hours",
        everyDay: "Every day",
        everyNDays: "Every {n} days",
        everyMonth: "Every month",
        recurring: "Recurring",
        timesFired: "{count} times fired",
        dailySlots: "{count} daily slots",
        activeStatus: "Active",
        until: "until",
        
        // Calendar
        noEvents: "No events found",
        upcomingEvents: "Upcoming Events",
        today: "Today",
        thisWeek: "This Week",
        eventsFor: "Events for",
        addEventsBot: "Add events through the Telegram or WhatsApp bot",
        start: "Start",
        end: "End",
        thisMonth: "This Month",
        totalEvents: "Total Events",
        daysWithEvents: "Days with Events",
        allDay: "All Day",
        
        // Calendar navigation
        previousMonth: "Previous month",
        nextMonth: "Next month",
        
        // Conversations
        noConversations: "No conversations found",
        chatWithAI: "Chat with AI",
        connected: "Connected",
        disconnected: "Disconnected",
        yourMessage: "Your message",
        aiResponse: "AI Response",
        recentConversations: "Recent Conversations",
        startChatting: "Start chatting with the SmartGTD bot on Telegram or WhatsApp",
        you: "You",
        smartgtdAI: "SmartGTD AI",
        totalMessages: "Total Messages",
        platformsUsed: "Platforms Used",
        last24h: "Last 24h",
        platformUsage: "Platform Usage",
        
        // Configuration
        configuration: "Configuration",
        platforms: "Platforms",
        platformsDesc: "Connect WhatsApp and other platforms",
        preferences: "Preferences",
        preferencesDesc: "Language, timezone, and personal settings",
        aiModels: "AI Models",
        aiModelsDesc: "Configure AI models and prompts",
        memory: "Memory",
        memoryDesc: "Memory strategy settings",
        memoryBackend: "Memory Backend",
        memoryStrategyDescription: "Choose which memory storage strategy to use for your account",
        memoryS3Vectors: "S3 Vectors (Basic)",
        memoryS3VectorsDesc: "Flat text phrases with semantic search — simple, fast, no entity extraction",
        memoryWiki: "LLM Wiki (Graph)",
        memoryWikiDesc: "Structured entity pages with relationships — entities, facts, and cross-references",
        memoryUnavailable: "unavailable",
        memoryProviderUpdated: "Memory provider updated successfully",
        memoryProviderFailed: "Failed to update memory provider",
        searchResult: "Result",
        searchResults: "Results",
        searchMatch: "match",
        memorySearchPlaceholder: "Search your memory...",
        memorySearchFailed: "Search failed",
        memorySearchNoResults: "No matches found",
        memorySearchMatchedFact: "matched",
        memorySearchFromPage: "from page",
        memorySearchRelated: "Related",
        memorySearchClear: "Clear",
        memoryViewList: "List",
        memoryViewGraph: "Graph",
        memoryEntities: "entities",
        memoryRelationships: "relationships",
        memoryEmpty: "No memories stored yet. Start chatting and your knowledge graph will build automatically.",
        integrations: "Integrations",
        integrationsDesc: "Connect external services",
        managePlatformsPreferences: "Manage your platforms and preferences",
        
        // Integrations
        googleTasksDesc: "Automatically sync your SmartGTD tasks with Google Tasks",
        googleCalendarDesc: "Sync scheduled tasks with Google Calendar events",
        notionDesc: "Export and sync tasks to Notion databases",
        slackDesc: "Receive task notifications and updates in Slack",
        productivity: "Productivity",
        calendarCategory: "Calendar",
        communication: "Communication",
        comingSoon: "Coming Soon",
        connect: "Connect",
        disconnect: "Disconnect",
        enable: "Enable",
        disable: "Disable",
        syncNow: "Sync Now",
        syncing: "Syncing...",
        connected: "Connected",
        enabled: "Enabled",
        disabled: "Disabled",
        notConnected: "Not Connected",
        integrationsSubtitle: "Connect SmartGTD with your favorite tools and services",
        failedToConnect: "Failed to connect",
        failedToDisconnect: "Failed to disconnect",
        failedToToggle: "Failed to toggle service",
        disconnectConfirm: "Disconnect this integration?",
        disableServiceConfirm: "Disable this service?",
        disconnectGoogleConfirm: "Disconnect Google? This will disable all Google services.",
        disconnectGoogle: "Disconnect Google",
        serviceEnabled: "Service enabled successfully",
        serviceDisabled: "Service disabled successfully",
        googleAccountConnected: "Google account connected. You can enable/disable individual services below.",
        calendarReauthRequired: "Please disconnect and reconnect Google to grant Calendar permissions.",
        syncedTasks: "Synced {count} tasks!",
        syncedEvents: "Synced {count} events!",
        syncFailed: "Sync failed",
        selectCalendar: "Select Calendar",
        selectCalendarDesc: "Choose which Google Calendar to sync with SmartGTD, or let us create a new one for you.",
        chooseCalendar: "-- Choose a calendar --",
        createNewCalendar: "📅 Create new \"SmartGTD\" calendar",
        selectCalendarError: "Please select a calendar",
        calendarSyncEnabled: "Calendar sync enabled!",
        watchChannelLastUpdate: "Watch channel last updated",
        refreshWatchChannel: "Refresh Watch Channel",
        refreshingWatchChannel: "Refreshing...",
        watchChannelRefreshed: "Watch channel refreshed successfully!",
        failedToRefreshWatch: "Failed to refresh watch channel",
        failedToEnableSync: "Failed to enable sync",
        cancel: "Cancel",
        confirm: "Confirm",
        syncingTo: "Syncing to",
        changeCalendar: "Change Calendar",
        
        // Platforms
        platformConnections: "Platform Connections",
        platformConnectionsDesc: "Connect your messaging platforms to use SmartGTD",
        whatsappConnectDesc: "Connect your WhatsApp number to interact with SmartGTD",
        botNumber: "Bot Number",
        yourNumber: "Your Number",
        sendVerificationCode: "Send Verification Code",
        invalidVerificationCode: "Invalid verification code",
        whatsappVerifiedSuccess: "WhatsApp verified successfully!",
        whatsappConnectedReady: "Your WhatsApp is connected and ready to use!",
        sendMessageToStart: "Send a message to start using SmartGTD via WhatsApp",
        failedToGenerateCode: "Failed to generate verification code. Please try again.",
        tooManyRequests: "Too many requests. Please wait a few minutes and try again.",
        pleasewait: "Please wait a few seconds before trying again",
        
        // Common
        enter: "Enter",
        
        // AI Models
        aiModel: "AI Model",
        selectModel: "Select a model...",
        chooseAIModel: "Choose the AI model for task processing",
        customSystemPrompt: "Custom System Prompt",
        overrideDefaultAI: "Override the default AI behavior with your own instructions",
        defineAIBehavior: "Define how the AI should behave and respond to your requests",
        usingDefaultPrompt: "Using default SmartGTD system prompt optimized for GTD task management",
        saveConfiguration: "Save Configuration",
        advancedAIConfigRequires: "Advanced AI configuration requires Power User or Admin access",
        contactAdminToUpgrade: "Contact your administrator to upgrade your account",
        configureAIBehavior: "Configure AI behavior and system prompts",
        userPreferences: "User Preferences",
        customizeExperience: "Customize your SmartGTD experience",
        noPreferencesYet: "No preferences set yet. Add some preferences by chatting with the bot!",
        useCommandsLike: "Use commands like",
        or: "or",
        saving: "Saving...",
        savePreferences: "Save Preferences",
        selectTimezone: "Select timezone",
        selectLanguage: "Select language",
        useCustomPrompt: "Use a custom prompt",
        systemPrompt: "System Prompt",
        custom: "Custom",
        resetToDefault: "Reset to Default",
        enterCustomPrompt: "Enter your custom system prompt...",
        systemPromptDescription: "This prompt defines how the AI assistant will behave. Changes will apply to new conversations.",
        saveSystemPrompt: "Save System Prompt",
        systemPromptUpdated: "System prompt updated successfully",
        failedToUpdateSystemPrompt: "Failed to update system prompt",
        systemPromptReset: "System prompt reset to default",
        failedToResetSystemPrompt: "Failed to reset system prompt",
        whatsappSetup: "WhatsApp Setup",
        telegramSetup: "Telegram Setup",
        phoneNumber: "Phone Number",
        username: "Username",
        verify: "Verify",
        verified: "Verified",
        notVerified: "Not Verified",
        sendCode: "Send Code",
        startConversation: "Start Conversation",
        changeNumber: "Change Number",
        connectWhatsApp: "Connect WhatsApp",
        clickStartConversationToVerify: "Click 'Start Conversation' to verify your WhatsApp number. When opening WhatsApp, you need to send the generated message to validate your user and phone number.",
        enterPhoneNumberPrompt: "Enter your WhatsApp phone number (with country code, e.g., +5519999999999):",
        whatsappVerificationMessage: "Please validate my whatsapp number. Verification code: {code}. (click send).",
        verificationCode: "Verification Code",
        includeCountryCode: "Include country code (e.g., +1234567890)",
        countryCode: "Country Code",
        selectCountry: "Select country",
        telegramUsernameNote: "Your Telegram username (without @)",
        note: "Note",
        startConversationFirst: "You must first start a conversation with our bot",
        enterVerificationCode: "Enter verification code",
        verifiedAndConfigured: "verified and configured",
        howVerificationWorks: "How verification works",
        whatsappVerificationSteps: "Click 'Start Conversation' → Send the verification code in your first message → AI confirms and sets up your account",
        onceVerified: "Once verified, send messages to SmartGTD via WhatsApp",
        getAIPowered: "Get AI-powered task management and productivity assistance",
        opening: "Opening...",
        conversationStarted: "Conversation Started!",
        sendVerificationCodeInWhatsApp: "Send the verification code in your first WhatsApp message to complete setup",
        nextSteps: "Next Steps",
        sendTheVerificationCode: "Send the verification code message in WhatsApp",
        aiWillConfirmAndSetup: "AI will confirm and set up your account automatically",
        startUsingSmartGTD: "Start using SmartGTD for task management",
        goToDashboard: "Go to Dashboard",
        
        // Chat
        chatSubtitle: "Ask me anything about your tasks, schedule, or productivity",
        typeMessage: "Type your message...",
        thinking: "Thinking...",
        usingTools: "Using tools ({count})...",
        connectionLost: "Connection lost. Reconnecting...",
        connectionRestored: "Connection restored",
        connected: "Connected",
        disconnectedReconnecting: "Disconnected - Reconnecting... ({current}/{max})",
        
        // History
        historyTitle: "History",
        historySubtitle: "View your conversation history",
        showEntries: "Show entries",
        
        // Onboarding
        welcomeToSmartGTD: "Welcome to SmartGTD!",
        connectMessagingPlatform: "Let's connect your messaging platform to get started with AI-powered task management.",
        useWhatsApp: "Use WhatsApp to manage your tasks",
        useTelegram: "Use Telegram to manage your tasks",
        setupLater: "Setup Later",
        continue: "Continue",
        enterPhoneNumber: "Enter Your Phone Number",
        enterUsername: "Enter Your Username",
        sendVerificationMessage: "We'll send you a message to verify your {platform} account.",
        phoneNumberPlaceholder: "555 123 4567",
        usernamePlaceholder: "@username",
        sendVerification: "Send Verification",
        sending: "Sending...",
        checkYourMessages: "Check Your Messages",
        verificationSent: "We've sent a verification message to your {platform}. Please enter the code below.",
        enterVerificationCode: "Enter verification code",
        verifying: "Verifying...",
        verifyComplete: "Verify & Complete",
        allSet: "All Set! 🎉",
        platformConnected: "Your {platform} is now connected. You can start managing your tasks with AI assistance!",
        nextSteps: "Next steps:",
        nextStepsMessage: "Send a message like 'Add buy groceries to my shopping list' to get started!",
        
        // Verification Messages
        whatsappVerificationSuccess: "✅ Verification successful! You can now start chatting with SmartGTD. Try asking: 'What can you do?'",
        whatsappWelcomeMessage: "🎉 Welcome to SmartGTD! I'm your AI-powered productivity assistant. I can help you manage tasks, reminders, and your schedule using GTD methodology. To get started, try sending me a message like: 'Add buy groceries to my shopping list' or 'Set reminder to call doctor tomorrow at 2pm'. Type 'help' to see more commands!",
        
        // Common
        loading: "Loading...",
        error: "Error",
        retry: "Retry",
        cancel: "Cancel",
        save: "Save",
        delete: "Delete",
        edit: "Edit",
        close: "Close",
        back: "Back",
        next: "Next",
        previous: "Previous",
        
        // Pricing & Subscription
        pricing: {
            title: "Choose Your Plan",
            subtitle: "Start your 3-day free trial - No credit card required",
            monthly: "Monthly",
            annual: "Annual",
            perMonth: "/month",
            perYear: "/year",
            save: "Save",
            freeTrial: "3-day free trial",
            feature1: "AI-powered task management",
            feature2: "Multi-platform access (Telegram, WhatsApp, Web)",
            feature3: "Smart scheduling and reminders",
            feature4: "Unlimited tasks and conversations",
            startTrial: "Start 3-Day Free Trial",
            trialInfo: "3-day free trial • No credit card required",
            alreadyHaveAccount: "Already have an account?",
            signIn: "Sign In"
        },
        
        trial: {
            daysRemaining: "{days} days left in your trial",
            endsToday: "Trial ends today!",
            addPayment: "Add Payment Method"
        },
        
        payment: {
            title: "Add Payment Method",
            trialRemaining: "{days} days remaining in your trial",
            selectedPlan: "Selected Plan",
            amount: "Amount",
            benefit1: "Continue using SmartGTD after trial",
            benefit2: "Cancel anytime, no questions asked",
            benefit3: "Secure payment powered by Stripe",
            continueBtn: "Continue to Payment",
            redirecting: "Redirecting to Stripe...",
            securePayment: "🔒 Secure payment powered by Stripe",
            error: "Error creating checkout session",
            noSubscription: "No subscription found",
            selectPlan: "Select a Plan"
        },
        
        auth: {
            signUp: "Sign Up",
            signIn: "Sign In",
            createAccount: "Create your account",
            welcomeBack: "Welcome back",
            confirmEmail: "Confirm your email",
            name: "Name",
            firstName: "First Name",
            lastName: "Last Name",
            username: "Username",
            phone: "Phone",
            email: "Email",
            password: "Password",
            processing: "Processing...",
            or: "OR",
            continueWithGoogle: "Continue with Google",
            alreadyHaveAccount: "Already have an account? Sign In",
            dontHaveAccount: "Don't have an account? Sign Up",
            backToPricing: "← Back to pricing",
            verificationSent: "We've sent a verification code to {email}. Please check your email and enter the code below.",
            verificationCode: "Verification Code",
            resendCode: "Resend Code",
            codeResent: "Verification code resent! Check your email.",
            emailConfirmed: "Email confirmed! You can now sign in."
        },
        
        onboarding: {
            trialBadge: "🎉 2 Days Free Trial",
            stepPlan: "Plan",
            stepAccount: "Account",
            stepSetup: "Setup"
        },
        
        subscription: {
            tab: "Subscription",
            tabDesc: "Manage your subscription and billing",
            title: "Subscription Management",
            currentPlan: "Current Plan",
            statusActive: "Active",
            statusTrial: "Trial",
            statusPastDue: "Payment Failed",
            statusExpired: "Expired",
            statusCanceled: "Canceled",
            statusIncomplete: "Incomplete",
            trialRemaining: "{days} days remaining in trial",
            addPaymentPrompt: "Add payment method to continue after trial",
            planDetails: "Plan Details",
            planType: "Plan Type",
            currency: "Currency",
            paymentMethod: "Payment Method",
            added: "Added",
            notAdded: "Not Added",
            manageButton: "Manage Subscription",
            managePayment: "Manage Payment Information",
            noSubscription: "No Active Subscription",
            noSubscriptionDesc: "You don't have an active subscription yet.",
            canceledTitle: "Subscription Canceled",
            canceledDesc: "Your subscription has been canceled. Reactivate anytime to continue using all features.",
            subscribe: "Subscribe Now",
            reactivate: "Reactivate Subscription",
            fetchError: "Failed to load subscription",
            portalError: "Failed to open billing portal",
            infoTitle: "Subscription Information:",
            info1: "Manage payment methods and billing details",
            info2: "View invoices and payment history",
            info3: "Cancel or update your subscription anytime",
            noSubscription: "No Active Subscription",
            noSubscriptionDesc: "You don't have an active subscription yet. Choose a plan to get started!",
            viewPlans: "View Plans",
            trialEnding: "Trial Ending Soon",
            trialActive: "Your trial is active",
            upgradeToPro: "Upgrade to Pro",
            subscriptionInactive: "Subscription Inactive",
            reactivateMessage: "Your subscription is not active. Reactivate to continue using SmartGTD.",
            accessLocked: "Access Locked",
            reactivateToAccess: "Your subscription was canceled. Reactivate to regain access to all features.",
            expiredMessage: "Your subscription has expired. Subscribe to continue using SmartGTD.",
            subscribeToAccess: "Subscribe to access SmartGTD features.",
            updatePayment: "Update Payment Method",
            subscriptionRequired: "Subscription Required",
            usageLimitExceeded: "Monthly Usage Limit Exceeded",
            activateToAccess: "Activate your subscription to access all features and data.",
            manageSubscription: "Manage your subscription",
            creditsUsage: "Credits Usage",
            credits: "credits",
            creditsCount: "{used} / {limit} credits",
            inputCredits: "Input",
            outputCredits: "Output",
            creditsWarning: "You're approaching your monthly credit limit. Consider upgrading your plan.",
            creditsLimitReached: "You have reached your monthly credit limit. Please upgrade your plan or wait until next month."
        },
        whatsapp: {
            setupTitle: "Connect WhatsApp",
            setupDescription: "Get instant task updates and manage your GTD workflow via WhatsApp",
            setupButton: "Setup WhatsApp"
        }
    },
    pt: {
        // Navigation
        dashboard: "Painel",
        tasks: "Tarefas",
        reminders: "Lembretes",
        calendar: "Calendário",
        conversations: "Chat",
        chat: "Chat",
        history: "Histórico",
        config: "Config",
        logout: "Sair",
        search: "Buscar",
        searchTasks: "Buscar tarefas...",
        
        // Dashboard
        welcomeBack: "Bem-vindo de volta",
        productivityOverview: "Aqui está sua visão geral de produtividade",
        activeTasks: "Tarefas Ativas",
        quickActions: "Ações Rápidas",
        recentActivity: "Atividade Recente",
        viewAll: "Ver Tudo",
        viewConversations: "Ver Conversas",
        recentConversationsText: "Suas conversas recentes aparecerão aqui",
        
        // Quick Actions
        manageTasks: "Gerencie suas tarefas GTD",
        viewReminders: "Veja lembretes próximos",
        checkSchedule: "Verifique sua agenda",
        chatHistory: "Histórico de chat com IA",
        integrations: "Integrações",
        integrationsDesc: "Configure integrações com Google Calendar e outros",
        
        // Login
        redirectingLogin: "Redirecionando para login seguro...",
        
        // Tasks
        noTasks: "Nenhuma tarefa encontrada",
        createTask: "Criar Tarefa",
        taskTitle: "Título da Tarefa",
        taskDescription: "Descrição",
        priority: "Prioridade",
        status: "Status",
        dueDate: "Data de Vencimento",
        yourTasks: "Suas Tarefas",
        filters: "Filtros",
        all: "Todas",
        nextAction: "Próxima Ação",
        scheduled: "Agendadas",
        waitingFor: "Aguardando",
        somedayMaybe: "Talvez/Algum Dia",
        active: "Ativas",
        completed: "Concluídas",
        due: "Vencimento",
        category: "Categoria",
        tryAdjustingFilters: "Tente ajustar seus filtros",
        startAddingTasks: "Comece adicionando tarefas através do bot do Telegram ou WhatsApp",
        
        // GTD Workflow
        gtdWorkflow: "Fluxo GTD",
        chooseFocusArea: "Escolha sua área de foco",
        nextActions: "Próximas Ações",
        doNow: "Fazer Agora",
        doLater: "Fazer Depois",
        delegated: "Delegado",
        someday: "Algum Dia",
        maybeLater: "Talvez Depois",
        nextActionsTasks: "Próximas Ações",
        scheduledTasks: "Tarefas Agendadas",
        waitingForTasks: "Aguardando",
        somedayMaybeTasks: "Talvez/Algum Dia",
        
        // Reminders
        noReminders: "Nenhum lembrete encontrado",
        upcomingReminders: "Próximos Lembretes",
        scheduledFor: "Agendado para",
        scheduledReminders: "Lembretes Agendados",
        next24h: "Próximas 24h",
        next24Hours: "Próximas 24 Horas",
        overdue: "Atrasados",
        overdueReminders: "Lembretes Atrasados",
        cancelledReminders: "Lembretes Cancelados",
        allReminders: "Todos os Lembretes",
        createRemindersBot: "Crie lembretes através do bot do Telegram ou WhatsApp",
        noRemindersFilter: "Nenhum lembrete {filter} no momento",
        created: "Criado",
        upcoming: "Próximos",
        
        // Recurring Reminders
        routines: "Rotinas",
        noRoutines: "Nenhuma rotina ainda",
        noRoutinesDesc: "Peça ao bot para criar lembretes recorrentes como:",
        noRoutinesExample: "\"Lembrar de tomar remédio todo dia às 8h, 14h, 21h\"",
        everyMinute: "A cada minuto",
        everyNMinutes: "A cada {n} minutos",
        everyHour: "A cada hora",
        everyNHours: "A cada {n} horas",
        everyDay: "Todo dia",
        everyNDays: "A cada {n} dias",
        everyMonth: "Todo mês",
        recurring: "Recorrente",
        timesFired: "{count} vezes disparado",
        dailySlots: "{count} horários diários",
        activeStatus: "Ativo",
        until: "até",
        
        // Calendar
        noEvents: "Nenhum evento encontrado",
        upcomingEvents: "Próximos Eventos",
        today: "Hoje",
        thisWeek: "Esta Semana",
        eventsFor: "Eventos para",
        addEventsBot: "Adicione eventos através do bot do Telegram ou WhatsApp",
        start: "Início",
        end: "Fim",
        thisMonth: "Este Mês",
        totalEvents: "Total de Eventos",
        daysWithEvents: "Dias com Eventos",
        allDay: "Dia Todo",
        
        // Calendar navigation
        previousMonth: "Mês anterior",
        nextMonth: "Próximo mês",
        
        // Conversations
        noConversations: "Nenhuma conversa encontrada",
        chatWithAI: "Chat com IA",
        connected: "Conectado",
        disconnected: "Desconectado",
        yourMessage: "Sua mensagem",
        aiResponse: "Resposta da IA",
        recentConversations: "Conversas Recentes",
        startChatting: "Comece a conversar com o bot SmartGTD no Telegram ou WhatsApp",
        you: "Você",
        smartgtdAI: "SmartGTD IA",
        totalMessages: "Total de Mensagens",
        platformsUsed: "Plataformas Usadas",
        last24h: "Últimas 24h",
        platformUsage: "Uso de Plataformas",
        
        // Configuration
        configuration: "Configuração",
        platforms: "Plataformas",
        platformsDesc: "Conecte WhatsApp e outras plataformas",
        preferences: "Preferências",
        preferencesDesc: "Idioma, fuso horário e configurações pessoais",
        aiModels: "Modelos de IA",
        aiModelsDesc: "Configure modelos de IA e prompts",
        memory: "Memória",
        memoryDesc: "Configurações de estratégia de memória",
        memoryBackend: "Backend de Memória",
        memoryStrategyDescription: "Escolha qual estratégia de armazenamento de memória usar na sua conta",
        memoryS3Vectors: "S3 Vectors (Básico)",
        memoryS3VectorsDesc: "Frases de texto com busca semântica — simples, rápido, sem extração de entidades",
        memoryWiki: "LLM Wiki (Grafo)",
        memoryWikiDesc: "Páginas de entidades estruturadas com relacionamentos — entidades, fatos e referências cruzadas",
        memoryUnavailable: "indisponível",
        memoryProviderUpdated: "Provedor de memória atualizado com sucesso",
        memoryProviderFailed: "Falha ao atualizar provedor de memória",
        searchResult: "Resultado",
        searchResults: "Resultados",
        searchMatch: "de correspondência",
        memorySearchPlaceholder: "Buscar na sua memória...",
        memorySearchFailed: "Falha na busca",
        memorySearchNoResults: "Nenhum resultado encontrado",
        memorySearchMatchedFact: "correspondência",
        memorySearchFromPage: "da página",
        memorySearchRelated: "Relacionados",
        memorySearchClear: "Limpar",
        memoryViewList: "Lista",
        memoryViewGraph: "Grafo",
        memoryEntities: "entidades",
        memoryRelationships: "relacionamentos",
        memoryEmpty: "Nenhuma memória armazenada ainda. Comece a conversar e seu grafo de conhecimento será construído automaticamente.",
        integrations: "Integrações",
        integrationsDesc: "Conecte serviços externos",
        managePlatformsPreferences: "Gerencie suas plataformas e preferências",
        
        // Integrations
        googleTasksDesc: "Sincronize automaticamente suas tarefas SmartGTD com Google Tasks",
        googleCalendarDesc: "Sincronize tarefas agendadas com eventos do Google Calendar",
        notionDesc: "Exporte e sincronize tarefas para bancos de dados do Notion",
        slackDesc: "Receba notificações e atualizações de tarefas no Slack",
        productivity: "Produtividade",
        calendarCategory: "Calendário",
        communication: "Comunicação",
        comingSoon: "Em Breve",
        connect: "Conectar",
        disconnect: "Desconectar",
        enable: "Ativar",
        disable: "Desativar",
        syncNow: "Sincronizar Agora",
        syncing: "Sincronizando...",
        connected: "Conectado",
        enabled: "Ativado",
        disabled: "Desativado",
        notConnected: "Não Conectado",
        integrationsSubtitle: "Conecte o SmartGTD com suas ferramentas e serviços favoritos",
        failedToConnect: "Falha ao conectar",
        failedToDisconnect: "Falha ao desconectar",
        failedToToggle: "Falha ao alternar serviço",
        disconnectConfirm: "Desconectar esta integração?",
        disableServiceConfirm: "Desativar este serviço?",
        disconnectGoogleConfirm: "Desconectar Google? Isso desativará todos os serviços do Google.",
        disconnectGoogle: "Desconectar Google",
        serviceEnabled: "Serviço ativado com sucesso",
        serviceDisabled: "Serviço desativado com sucesso",
        googleAccountConnected: "Conta Google conectada. Você pode ativar/desativar serviços individuais abaixo.",
        calendarReauthRequired: "Por favor, desconecte e reconecte o Google para conceder permissões de Calendário.",
        syncedTasks: "{count} tarefas sincronizadas!",
        syncedEvents: "{count} eventos sincronizados!",
        syncFailed: "Falha na sincronização",
        selectCalendar: "Selecionar Calendário",
        selectCalendarDesc: "Escolha qual Google Calendar sincronizar com o SmartGTD, ou deixe-nos criar um novo para você.",
        chooseCalendar: "-- Escolha um calendário --",
        createNewCalendar: "📅 Criar novo calendário \"SmartGTD\"",
        selectCalendarError: "Por favor, selecione um calendário",
        calendarSyncEnabled: "Sincronização de calendário ativada!",
        watchChannelLastUpdate: "Canal de observação atualizado pela última vez",
        refreshWatchChannel: "Atualizar Canal de Observação",
        refreshingWatchChannel: "Atualizando...",
        watchChannelRefreshed: "Canal de observação atualizado com sucesso!",
        failedToRefreshWatch: "Falha ao atualizar canal de observação",
        failedToEnableSync: "Falha ao ativar sincronização",
        cancel: "Cancelar",
        confirm: "Confirmar",
        syncingTo: "Sincronizando com",
        changeCalendar: "Alterar Calendário",
        
        // Platforms
        platformConnections: "Conexões de Plataforma",
        platformConnectionsDesc: "Conecte suas plataformas de mensagens para usar o SmartGTD",
        whatsappConnectDesc: "Conecte seu número do WhatsApp para interagir com o SmartGTD",
        botNumber: "Número do Bot",
        yourNumber: "Seu Número",
        sendVerificationCode: "Enviar Código de Verificação",
        invalidVerificationCode: "Código de verificação inválido",
        whatsappVerifiedSuccess: "WhatsApp verificado com sucesso!",
        whatsappConnectedReady: "Seu WhatsApp está conectado e pronto para usar!",
        sendMessageToStart: "Envie uma mensagem para começar a usar o SmartGTD via WhatsApp",
        failedToGenerateCode: "Falha ao gerar código de verificação. Tente novamente.",
        tooManyRequests: "Muitas solicitações. Aguarde alguns minutos e tente novamente.",
        pleasewait: "Aguarde alguns segundos antes de tentar novamente",
        
        // Common
        enter: "Digite",
        
        // AI Models
        aiModel: "Modelo de IA",
        selectModel: "Selecione um modelo...",
        chooseAIModel: "Escolha o modelo de IA para processamento de tarefas",
        customSystemPrompt: "Prompt Personalizado do Sistema",
        overrideDefaultAI: "Substitua o comportamento padrão da IA com suas próprias instruções",
        defineAIBehavior: "Defina como a IA deve se comportar e responder às suas solicitações",
        usingDefaultPrompt: "Usando prompt padrão do SmartGTD otimizado para gerenciamento de tarefas GTD",
        saveConfiguration: "Salvar Configuração",
        advancedAIConfigRequires: "Configuração avançada de IA requer acesso de Power User ou Admin",
        contactAdminToUpgrade: "Entre em contato com seu administrador para atualizar sua conta",
        configureAIBehavior: "Configure o comportamento da IA e prompts do sistema",
        userPreferences: "Preferências do Usuário",
        customizeExperience: "Personalize sua experiência SmartGTD",
        noPreferencesYet: "Nenhuma preferência definida ainda. Adicione algumas preferências conversando com o bot!",
        useCommandsLike: "Use comandos como",
        or: "ou",
        saving: "Salvando...",
        savePreferences: "Salvar Preferências",
        selectTimezone: "Selecionar fuso horário",
        selectLanguage: "Selecionar idioma",
        useCustomPrompt: "Usar um prompt personalizado",
        systemPrompt: "Prompt do Sistema",
        custom: "Personalizado",
        resetToDefault: "Redefinir para Padrão",
        enterCustomPrompt: "Digite seu prompt personalizado do sistema...",
        systemPromptDescription: "Este prompt define como o assistente de IA se comportará. As alterações se aplicarão a novas conversas.",
        saveSystemPrompt: "Salvar Prompt do Sistema",
        systemPromptUpdated: "Prompt do sistema atualizado com sucesso",
        failedToUpdateSystemPrompt: "Falha ao atualizar prompt do sistema",
        systemPromptReset: "Prompt do sistema redefinido para padrão",
        failedToResetSystemPrompt: "Falha ao redefinir prompt do sistema",
        whatsappSetup: "Configuração WhatsApp",
        telegramSetup: "Configuração Telegram",
        phoneNumber: "Número de Telefone",
        username: "Nome de Usuário",
        verify: "Verificar",
        verified: "Verificado",
        notVerified: "Não Verificado",
        sendCode: "Enviar Código",
        startConversation: "Iniciar Conversa",
        changeNumber: "Alterar Número",
        connectWhatsApp: "Conectar WhatsApp",
        clickStartConversationToVerify: "Clique em 'Iniciar Conversa' para verificar seu número do WhatsApp. Ao abrir o WhatsApp, você precisa enviar a mensagem gerada para verificação do seu número e usuário.",
        enterPhoneNumberPrompt: "Digite seu número do WhatsApp (com código do país, ex: +5519999999999):",
        whatsappVerificationMessage: "Por favor, valide meu número do WhatsApp. Código de verificação: {code}. (clique em enviar).",
        verificationCode: "Código de Verificação",
        includeCountryCode: "Inclua o código do país (ex: +5511987654321)",
        countryCode: "Código do País",
        selectCountry: "Selecionar país",
        telegramUsernameNote: "Seu nome de usuário do Telegram (sem @)",
        note: "Nota",
        startConversationFirst: "Você deve primeiro iniciar uma conversa com nosso bot",
        enterVerificationCode: "Digite o código de verificação",
        verifiedAndConfigured: "verificado e configurado",
        howVerificationWorks: "Como funciona a verificação",
        whatsappVerificationSteps: "Clique em 'Iniciar Conversa' → Envie o código de verificação na sua primeira mensagem → A IA confirma e configura sua conta",
        onceVerified: "Uma vez verificado, envie mensagens para o SmartGTD via WhatsApp",
        getAIPowered: "Obtenha gerenciamento de tarefas e assistência de produtividade com IA",
        opening: "Abrindo...",
        conversationStarted: "Conversa Iniciada!",
        sendVerificationCodeInWhatsApp: "Envie o código de verificação na sua primeira mensagem do WhatsApp para concluir a configuração",
        nextSteps: "Próximos Passos",
        sendTheVerificationCode: "Envie a mensagem com o código de verificação no WhatsApp",
        aiWillConfirmAndSetup: "A IA confirmará e configurará sua conta automaticamente",
        startUsingSmartGTD: "Comece a usar o SmartGTD para gerenciamento de tarefas",
        goToDashboard: "Ir para o Painel",
        getAIPowered: "Obtenha gerenciamento de tarefas e assistência de produtividade com IA",
        
        // Chat
        chatSubtitle: "Pergunte-me qualquer coisa sobre suas tarefas, agenda ou produtividade",
        typeMessage: "Digite sua mensagem...",
        thinking: "Pensando...",
        usingTools: "Usando ferramentas ({count})...",
        connectionLost: "Conexão perdida. Reconectando...",
        connectionRestored: "Conexão restaurada",
        connected: "Conectado",
        disconnectedReconnecting: "Desconectado - Reconectando... ({current}/{max})",
        
        // History
        historyTitle: "Histórico",
        historySubtitle: "Visualize seu histórico de conversas",
        showEntries: "Mostrar entradas",
        
        // Onboarding
        welcomeToSmartGTD: "Bem-vindo ao SmartGTD!",
        connectMessagingPlatform: "Vamos conectar sua plataforma de mensagens para começar com o gerenciamento de tarefas com IA.",
        useWhatsApp: "Use WhatsApp para gerenciar suas tarefas",
        useTelegram: "Use Telegram para gerenciar suas tarefas",
        setupLater: "Configurar Depois",
        continue: "Continuar",
        enterPhoneNumber: "Digite Seu Número de Telefone",
        enterUsername: "Digite Seu Nome de Usuário",
        sendVerificationMessage: "Enviaremos uma mensagem para verificar sua conta do {platform}.",
        phoneNumberPlaceholder: "11 99999 9999",
        usernamePlaceholder: "@usuario",
        sendVerification: "Enviar Verificação",
        sending: "Enviando...",
        checkYourMessages: "Verifique Suas Mensagens",
        verificationSent: "Enviamos uma mensagem de verificação para seu {platform}. Digite o código abaixo.",
        enterVerificationCode: "Digite o código de verificação",
        verifying: "Verificando...",
        verifyComplete: "Verificar e Concluir",
        allSet: "Tudo Pronto! 🎉",
        platformConnected: "Seu {platform} está conectado. Você pode começar a gerenciar suas tarefas com assistência de IA!",
        nextSteps: "Próximos passos:",
        nextStepsMessage: "Envie uma mensagem como 'Adicionar comprar mantimentos à minha lista de compras' para começar!",
        
        // Verification Messages
        whatsappVerificationSuccess: "✅ Verificação bem-sucedida! Você pode começar a conversar com o SmartGTD. Experimente perguntar: 'O que você pode fazer?'",
        whatsappWelcomeMessage: "🎉 Bem-vindo ao SmartGTD! Sou seu assistente de produtividade com IA. Posso ajudá-lo a gerenciar tarefas, lembretes e sua agenda usando a metodologia GTD. Para começar, experimente me enviar uma mensagem como: 'Adicionar comprar mantimentos à minha lista de compras' ou 'Definir lembrete para ligar para o médico amanhã às 14h'. Digite 'ajuda' para ver mais comandos!",
        
        // Common
        loading: "Carregando...",
        error: "Erro",
        retry: "Tentar Novamente",
        cancel: "Cancelar",
        save: "Salvar",
        delete: "Excluir",
        
        // Pricing & Subscription
        pricing: {
            title: "Escolha Seu Plano",
            subtitle: "Comece seu teste grátis de 3 dias - Sem cartão de crédito",
            monthly: "Mensal",
            annual: "Anual",
            perMonth: "/mês",
            perYear: "/ano",
            save: "Economize",
            feature1: "Gerenciamento de tarefas com IA",
            feature2: "Acesso multiplataforma (Telegram, WhatsApp, Web)",
            feature3: "Agendamento inteligente e lembretes",
            feature4: "Tarefas e conversas ilimitadas",
            startTrial: "Iniciar Teste Grátis de 3 Dias",
            trialInfo: "3 dias de teste grátis • Sem cartão de crédito",
            alreadyHaveAccount: "Já tem uma conta?",
            signIn: "Entrar"
        },
        
        trial: {
            daysRemaining: "{days} dias restantes no seu teste",
            endsToday: "Teste termina hoje!",
            addPayment: "Adicionar Forma de Pagamento"
        },
        
        payment: {
            title: "Adicionar Forma de Pagamento",
            trialRemaining: "{days} dias restantes no seu teste",
            selectedPlan: "Plano Selecionado",
            amount: "Valor",
            benefit1: "Continue usando SmartGTD após o teste",
            benefit2: "Cancele quando quiser, sem perguntas",
            benefit3: "Pagamento seguro via Stripe",
            continueBtn: "Continuar para Pagamento",
            redirecting: "Redirecionando para Stripe...",
            securePayment: "🔒 Pagamento seguro via Stripe",
            error: "Erro ao criar sessão de pagamento",
            noSubscription: "Nenhuma assinatura encontrada",
            selectPlan: "Selecionar um Plano"
        },
        
        auth: {
            signUp: "Cadastrar",
            signIn: "Entrar",
            createAccount: "Crie sua conta",
            welcomeBack: "Bem-vindo de volta",
            confirmEmail: "Confirme seu e-mail",
            name: "Nome",
            firstName: "Primeiro Nome",
            lastName: "Sobrenome",
            username: "Nome de usuário",
            phone: "Telefone",
            email: "E-mail",
            password: "Senha",
            processing: "Processando...",
            or: "OU",
            continueWithGoogle: "Continuar com Google",
            alreadyHaveAccount: "Já tem uma conta? Entrar",
            dontHaveAccount: "Não tem uma conta? Cadastrar",
            backToPricing: "← Voltar para planos",
            verificationSent: "Enviamos um código de verificação para {email}. Por favor, verifique seu e-mail e digite o código abaixo.",
            verificationCode: "Código de Verificação",
            resendCode: "Reenviar Código",
            codeResent: "Código de verificação reenviado! Verifique seu e-mail.",
            emailConfirmed: "E-mail confirmado! Agora você pode entrar."
        },
        
        onboarding: {
            trialBadge: "🎉 2 Dias Grátis",
            stepPlan: "Plano",
            stepAccount: "Conta",
            stepSetup: "Configurar"
        },
        
        subscription: {
            tab: "Assinatura",
            tabDesc: "Gerencie sua assinatura e cobrança",
            title: "Gerenciamento de Assinatura",
            currentPlan: "Plano Atual",
            statusActive: "Ativo",
            statusTrial: "Teste",
            statusPastDue: "Pagamento Falhou",
            statusExpired: "Expirado",
            statusCanceled: "Cancelado",
            statusIncomplete: "Incompleto",
            trialRemaining: "{days} dias restantes no teste",
            addPaymentPrompt: "Adicione método de pagamento para continuar após o teste",
            planDetails: "Detalhes do Plano",
            planType: "Tipo de Plano",
            currency: "Moeda",
            paymentMethod: "Método de Pagamento",
            added: "Adicionado",
            notAdded: "Não Adicionado",
            manageButton: "Gerenciar Assinatura",
            managePayment: "Gerenciar Informações de Pagamento",
            noSubscription: "Sem Assinatura Ativa",
            noSubscriptionDesc: "Você ainda não possui uma assinatura ativa.",
            canceledTitle: "Assinatura Cancelada",
            canceledDesc: "Sua assinatura foi cancelada. Reative a qualquer momento para continuar usando todos os recursos.",
            subscribe: "Assinar Agora",
            reactivate: "Reativar Assinatura",
            fetchError: "Falha ao carregar assinatura",
            portalError: "Falha ao abrir portal de cobrança",
            infoTitle: "Informações da Assinatura:",
            info1: "Gerencie métodos de pagamento e detalhes de cobrança",
            info2: "Visualize faturas e histórico de pagamentos",
            info3: "Cancele ou atualize sua assinatura a qualquer momento",
            trialEnding: "Teste Terminando em Breve",
            trialActive: "Seu teste está ativo",
            upgradeToPro: "Atualizar para Pro",
            subscriptionInactive: "Assinatura Inativa",
            reactivateMessage: "Sua assinatura não está ativa. Reative para continuar usando o SmartGTD.",
            accessLocked: "Acesso Bloqueado",
            reactivateToAccess: "Sua assinatura foi cancelada. Reative para recuperar o acesso a todos os recursos.",
            expiredMessage: "Sua assinatura expirou. Assine para continuar usando o SmartGTD.",
            subscribeToAccess: "Assine para acessar os recursos do SmartGTD.",
            updatePayment: "Atualizar Método de Pagamento",
            subscriptionRequired: "Assinatura Necessária",
            usageLimitExceeded: "Limite Mensal de Uso Excedido",
            activateToAccess: "Ative sua assinatura para acessar todos os recursos e dados.",
            noSubscription: "Nenhuma Assinatura Ativa",
            noSubscriptionDesc: "Você ainda não tem uma assinatura ativa. Escolha um plano para começar!",
            viewPlans: "Ver Planos",
            manageSubscription: "Gerenciar sua assinatura",
            creditsUsage: "Uso de Créditos",
            credits: "créditos",
            creditsCount: "{used} / {limit} créditos",
            inputCredits: "Entrada",
            outputCredits: "Saída",
            creditsWarning: "Você está se aproximando do limite mensal de créditos. Considere atualizar seu plano.",
            creditsLimitReached: "Você atingiu seu limite mensal de créditos. Por favor, atualize seu plano ou aguarde até o próximo mês."
        },
        whatsapp: {
            setupTitle: "Conectar WhatsApp",
            setupDescription: "Receba atualizações instantâneas de tarefas e gerencie seu fluxo GTD via WhatsApp",
            setupButton: "Configurar WhatsApp"
        },
        
        edit: "Editar",
        close: "Fechar",
        back: "Voltar",
        next: "Próximo",
        previous: "Anterior"
    },
    es: {
        // Navigation
        dashboard: "Panel",
        tasks: "Tareas",
        reminders: "Recordatorios",
        calendar: "Calendario",
        conversations: "Chat",
        chat: "Chat",
        history: "Historial",
        config: "Config",
        logout: "Salir",
        search: "Buscar",
        searchTasks: "Buscar tareas...",
        
        // Dashboard
        welcomeBack: "¡Bienvenido de vuelta!",
        productivityOverview: "Aquí está tu resumen de productividad",
        activeTasks: "Tareas Activas",
        quickActions: "Acciones Rápidas",
        recentActivity: "Actividad Reciente",
        viewAll: "Ver Todo",
        viewConversations: "Ver Conversaciones",
        recentConversationsText: "Tus conversaciones recientes aparecerán aquí",
        
        // Quick Actions
        manageTasks: "Gestiona tus tareas GTD",
        viewReminders: "Ver recordatorios próximos",
        checkSchedule: "Revisa tu horario",
        chatHistory: "Historial de chat con IA",
        integrations: "Integraciones",
        integrationsDesc: "Configura integraciones con Google Calendar y otros",
        
        // Login
        redirectingLogin: "Redirigiendo a login seguro...",
        
        // Tasks
        noTasks: "No se encontraron tareas",
        createTask: "Crear Tarea",
        taskTitle: "Título de la Tarea",
        taskDescription: "Descripción",
        priority: "Prioridad",
        status: "Estado",
        dueDate: "Fecha de Vencimiento",
        yourTasks: "Tus Tareas",
        filters: "Filtros",
        all: "Todas",
        nextAction: "Próxima Acción",
        scheduled: "Programadas",
        waitingFor: "Esperando",
        somedayMaybe: "Algún Día/Quizás",
        active: "Activas",
        completed: "Completadas",
        due: "Vencimiento",
        category: "Categoría",
        tryAdjustingFilters: "Intenta ajustar tus filtros",
        startAddingTasks: "Comienza agregando tareas a través del bot de Telegram o WhatsApp",
        
        // GTD Workflow
        gtdWorkflow: "Flujo GTD",
        chooseFocusArea: "Elige tu área de enfoque",
        nextActions: "Próximas Acciones",
        doNow: "Hacer Ahora",
        doLater: "Hacer Después",
        delegated: "Delegado",
        someday: "Algún Día",
        maybeLater: "Quizás Después",
        nextActionsTasks: "Próximas Acciones",
        scheduledTasks: "Tareas Programadas",
        waitingForTasks: "Esperando",
        somedayMaybeTasks: "Algún Día/Quizás",
        
        // Reminders
        noReminders: "No se encontraron recordatorios",
        upcomingReminders: "Próximos Recordatorios",
        scheduledFor: "Programado para",
        scheduledReminders: "Recordatorios Programados",
        next24h: "Próximas 24h",
        next24Hours: "Próximas 24 Horas",
        overdue: "Vencidos",
        overdueReminders: "Recordatorios Vencidos",
        cancelledReminders: "Recordatorios Cancelados",
        allReminders: "Todos los Recordatorios",
        createRemindersBot: "Crea recordatorios a través del bot de Telegram o WhatsApp",
        noRemindersFilter: "No hay recordatorios {filter} en este momento",
        created: "Creado",
        upcoming: "Próximos",
        
        // Recurring Reminders
        routines: "Rutinas",
        noRoutines: "No hay rutinas aún",
        noRoutinesDesc: "Pide al bot crear recordatorios recurrentes como:",
        noRoutinesExample: "\"Recordarme tomar medicación todos los días a las 8h, 14h, 21h\"",
        everyMinute: "Cada minuto",
        everyNMinutes: "Cada {n} minutos",
        everyHour: "Cada hora",
        everyNHours: "Cada {n} horas",
        everyDay: "Todos los días",
        everyNDays: "Cada {n} días",
        everyMonth: "Cada mes",
        recurring: "Recurrente",
        timesFired: "{count} veces disparado",
        dailySlots: "{count} horarios diarios",
        activeStatus: "Activo",
        until: "hasta",
        
        // Calendar
        noEvents: "No se encontraron eventos",
        upcomingEvents: "Próximos Eventos",
        today: "Hoy",
        thisWeek: "Esta Semana",
        eventsFor: "Eventos para",
        addEventsBot: "Agrega eventos a través del bot de Telegram o WhatsApp",
        start: "Inicio",
        end: "Fin",
        thisMonth: "Este Mes",
        totalEvents: "Total de Eventos",
        daysWithEvents: "Días con Eventos",
        allDay: "Todo el Día",
        
        // Calendar navigation
        previousMonth: "Mes anterior",
        nextMonth: "Próximo mes",
        
        // Conversations
        noConversations: "No se encontraron conversaciones",
        chatWithAI: "Chat con IA",
        connected: "Conectado",
        disconnected: "Desconectado",
        yourMessage: "Tu mensaje",
        aiResponse: "Respuesta de IA",
        recentConversations: "Conversaciones Recientes",
        startChatting: "Comienza a chatear con el bot SmartGTD en Telegram o WhatsApp",
        you: "Tú",
        smartgtdAI: "SmartGTD IA",
        totalMessages: "Total de Mensajes",
        platformsUsed: "Plataformas Usadas",
        last24h: "Últimas 24h",
        platformUsage: "Uso de Plataformas",
        
        // Configuration
        configuration: "Configuración",
        platforms: "Plataformas",
        platformsDesc: "Conecta WhatsApp y otras plataformas",
        preferences: "Preferencias",
        preferencesDesc: "Idioma, zona horaria y configuración personal",
        aiModels: "Modelos de IA",
        aiModelsDesc: "Configura modelos de IA y prompts",
        memory: "Memoria",
        memoryDesc: "Configuración de estrategia de memoria",
        memoryBackend: "Backend de Memoria",
        memoryStrategyDescription: "Elige qué estrategia de almacenamiento de memoria usar en tu cuenta",
        memoryS3Vectors: "S3 Vectors (Básico)",
        memoryS3VectorsDesc: "Frases de texto con búsqueda semántica — simple, rápido, sin extracción de entidades",
        memoryWiki: "LLM Wiki (Grafo)",
        memoryWikiDesc: "Páginas de entidades estructuradas con relaciones — entidades, hechos y referencias cruzadas",
        memoryUnavailable: "no disponible",
        memoryProviderUpdated: "Proveedor de memoria actualizado correctamente",
        memoryProviderFailed: "Error al actualizar proveedor de memoria",
        searchResult: "Resultado",
        searchResults: "Resultados",
        searchMatch: "de coincidencia",
        memorySearchPlaceholder: "Buscar en tu memoria...",
        memorySearchFailed: "Error en la búsqueda",
        memorySearchNoResults: "No se encontraron resultados",
        memorySearchMatchedFact: "coincidencia",
        memorySearchFromPage: "de la página",
        memorySearchRelated: "Relacionados",
        memorySearchClear: "Limpiar",
        memoryViewList: "Lista",
        memoryViewGraph: "Grafo",
        memoryEntities: "entidades",
        memoryRelationships: "relaciones",
        memoryEmpty: "Aún no hay memorias almacenadas. Empieza a conversar y tu grafo de conocimiento se construirá automáticamente.",
        integrations: "Integraciones",
        integrationsDesc: "Conecta servicios externos",
        managePlatformsPreferences: "Gestiona tus plataformas y preferencias",
        
        // Integrations
        googleTasksDesc: "Sincroniza automáticamente tus tareas SmartGTD con Google Tasks",
        googleCalendarDesc: "Sincroniza tareas programadas con eventos de Google Calendar",
        notionDesc: "Exporta y sincroniza tareas a bases de datos de Notion",
        slackDesc: "Recibe notificaciones y actualizaciones de tareas en Slack",
        productivity: "Productividad",
        calendarCategory: "Calendario",
        communication: "Comunicación",
        comingSoon: "Próximamente",
        connect: "Conectar",
        disconnect: "Desconectar",
        enable: "Activar",
        disable: "Desactivar",
        syncNow: "Sincronizar Ahora",
        syncing: "Sincronizando...",
        connected: "Conectado",
        enabled: "Activado",
        disabled: "Desactivado",
        notConnected: "No Conectado",
        integrationsSubtitle: "Conecta SmartGTD con tus herramientas y servicios favoritos",
        failedToConnect: "Error al conectar",
        failedToDisconnect: "Error al desconectar",
        failedToToggle: "Error al alternar servicio",
        disconnectConfirm: "¿Desconectar esta integración?",
        disableServiceConfirm: "¿Desactivar este servicio?",
        disconnectGoogleConfirm: "¿Desconectar Google? Esto desactivará todos los servicios de Google.",
        disconnectGoogle: "Desconectar Google",
        serviceEnabled: "Servicio activado exitosamente",
        serviceDisabled: "Servicio desactivado exitosamente",
        googleAccountConnected: "Cuenta de Google conectada. Puedes activar/desactivar servicios individuales abajo.",
        calendarReauthRequired: "Por favor, desconecta y reconecta Google para otorgar permisos de Calendario.",
        syncedTasks: "¡{count} tareas sincronizadas!",
        syncedEvents: "¡{count} eventos sincronizados!",
        syncFailed: "Error en la sincronización",
        syncNow: "Sincronizar Ahora",
        syncing: "Sincronizando...",
        connected: "Conectado",
        notConnected: "No Conectado",
        integrationsSubtitle: "Conecta SmartGTD con tus herramientas y servicios favoritos",
        failedToConnect: "Error al conectar",
        failedToDisconnect: "Error al desconectar",
        disconnectConfirm: "¿Desconectar esta integración?",
        syncedTasks: "¡{count} tareas sincronizadas!",
        syncFailed: "Error en la sincronización",
        selectCalendar: "Seleccionar Calendario",
        selectCalendarDesc: "Elige qué Google Calendar sincronizar con SmartGTD, o déjanos crear uno nuevo para ti.",
        chooseCalendar: "-- Elige un calendario --",
        createNewCalendar: "📅 Crear nuevo calendario \"SmartGTD\"",
        selectCalendarError: "Por favor, selecciona un calendario",
        calendarSyncEnabled: "¡Sincronización de calendario activada!",
        watchChannelLastUpdate: "Canal de observación actualizado por última vez",
        refreshWatchChannel: "Actualizar Canal de Observación",
        refreshingWatchChannel: "Actualizando...",
        watchChannelRefreshed: "¡Canal de observación actualizado exitosamente!",
        failedToRefreshWatch: "Error al actualizar canal de observación",
        failedToEnableSync: "Error al activar sincronización",
        cancel: "Cancelar",
        confirm: "Confirmar",
        syncingTo: "Sincronizando con",
        changeCalendar: "Cambiar Calendario",
        
        // Platforms
        platformConnections: "Conexiones de Plataforma",
        platformConnectionsDesc: "Conecta tus plataformas de mensajería para usar SmartGTD",
        whatsappConnectDesc: "Conecta tu número de WhatsApp para interactuar con SmartGTD",
        botNumber: "Número del Bot",
        yourNumber: "Tu Número",
        sendVerificationCode: "Enviar Código de Verificación",
        invalidVerificationCode: "Código de verificación inválido",
        whatsappVerifiedSuccess: "¡WhatsApp verificado exitosamente!",
        whatsappConnectedReady: "¡Tu WhatsApp está conectado y listo para usar!",
        sendMessageToStart: "Envía un mensaje para comenzar a usar SmartGTD vía WhatsApp",
        failedToGenerateCode: "Error al generar código de verificación. Inténtalo de nuevo.",
        tooManyRequests: "Demasiadas solicitudes. Espera unos minutos e inténtalo de nuevo.",
        pleasewait: "Espera unos segundos antes de intentarlo de nuevo",
        
        // Common
        enter: "Ingresa",
        
        // AI Models
        aiModel: "Modelo de IA",
        selectModel: "Selecciona un modelo...",
        chooseAIModel: "Elige el modelo de IA para procesamiento de tareas",
        customSystemPrompt: "Prompt Personalizado del Sistema",
        overrideDefaultAI: "Reemplaza el comportamiento predeterminado de la IA con tus propias instrucciones",
        defineAIBehavior: "Define cómo debe comportarse la IA y responder a tus solicitudes",
        usingDefaultPrompt: "Usando prompt predeterminado de SmartGTD optimizado para gestión de tareas GTD",
        saveConfiguration: "Guardar Configuración",
        advancedAIConfigRequires: "La configuración avanzada de IA requiere acceso de Power User o Admin",
        contactAdminToUpgrade: "Contacta a tu administrador para actualizar tu cuenta",
        configureAIBehavior: "Configura el comportamiento de la IA y prompts del sistema",
        userPreferences: "Preferencias del Usuario",
        customizeExperience: "Personaliza tu experiencia SmartGTD",
        noPreferencesYet: "No hay preferencias configuradas aún. ¡Agrega algunas preferencias chateando con el bot!",
        useCommandsLike: "Usa comandos como",
        or: "o",
        saving: "Guardando...",
        savePreferences: "Guardar Preferencias",
        selectTimezone: "Seleccionar zona horaria",
        selectLanguage: "Seleccionar idioma",
        useCustomPrompt: "Usar un prompt personalizado",
        systemPrompt: "Prompt del Sistema",
        custom: "Personalizado",
        resetToDefault: "Restablecer a Predeterminado",
        enterCustomPrompt: "Ingresa tu prompt personalizado del sistema...",
        systemPromptDescription: "Este prompt define cómo se comportará el asistente de IA. Los cambios se aplicarán a nuevas conversaciones.",
        saveSystemPrompt: "Guardar Prompt del Sistema",
        systemPromptUpdated: "Prompt del sistema actualizado exitosamente",
        failedToUpdateSystemPrompt: "Error al actualizar prompt del sistema",
        systemPromptReset: "Prompt del sistema restablecido a predeterminado",
        failedToResetSystemPrompt: "Error al restablecer prompt del sistema",
        whatsappSetup: "Configuración WhatsApp",
        telegramSetup: "Configuración Telegram",
        phoneNumber: "Número de Teléfono",
        username: "Nombre de Usuario",
        verify: "Verificar",
        verified: "Verificado",
        notVerified: "No Verificado",
        sendCode: "Enviar Código",
        startConversation: "Iniciar Conversación",
        changeNumber: "Cambiar Número",
        connectWhatsApp: "Conectar WhatsApp",
        clickStartConversationToVerify: "Haga clic en 'Iniciar Conversación' para verificar su número de WhatsApp. Al abrir WhatsApp, debes enviar el mensaje generado para validar tu usuario y número de teléfono.",
        enterPhoneNumberPrompt: "Ingrese su número de WhatsApp (con código de país, ej: +34612345678):",
        whatsappVerificationMessage: "Por favor, valide mi número de WhatsApp. Código de verificación: {code}. (haga clic en enviar).",
        verificationCode: "Código de Verificación",
        includeCountryCode: "Incluye el código de país (ej: +34612345678)",
        countryCode: "Código de País",
        selectCountry: "Seleccionar país",
        telegramUsernameNote: "Tu nombre de usuario de Telegram (sin @)",
        note: "Nota",
        startConversationFirst: "Primero debes iniciar una conversación con nuestro bot",
        enterVerificationCode: "Ingresa el código de verificación",
        verifiedAndConfigured: "verificado y configurado",
        howVerificationWorks: "Cómo funciona la verificación",
        whatsappVerificationSteps: "Haz clic en 'Iniciar Conversación' → Envía el código de verificación en tu primer mensaje → La IA confirma y configura tu cuenta",
        onceVerified: "Una vez verificado, envía mensajes a SmartGTD vía WhatsApp",
        getAIPowered: "Obtén gestión de tareas y asistencia de productividad con IA",
        opening: "Abriendo...",
        conversationStarted: "¡Conversación Iniciada!",
        sendVerificationCodeInWhatsApp: "Envía el código de verificación en tu primer mensaje de WhatsApp para completar la configuración",
        nextSteps: "Próximos Pasos",
        sendTheVerificationCode: "Envía el mensaje con el código de verificación en WhatsApp",
        aiWillConfirmAndSetup: "La IA confirmará y configurará tu cuenta automáticamente",
        startUsingSmartGTD: "Comienza a usar SmartGTD para gestión de tarefas",
        goToDashboard: "Ir al Panel",
        getAIPowered: "Obtén gestión de tareas y asistencia de productividad con IA",
        
        // Chat
        chatSubtitle: "Pregúntame cualquier cosa sobre tus tareas, horario o productividad",
        typeMessage: "Escribe tu mensaje...",
        thinking: "Pensando...",
        usingTools: "Usando herramientas ({count})...",
        connectionLost: "Conexión perdida. Reconectando...",
        connectionRestored: "Conexión restaurada",
        connected: "Conectado",
        disconnectedReconnecting: "Desconectado - Reconectando... ({current}/{max})",
        
        // History
        historyTitle: "Historial",
        historySubtitle: "Ver tu historial de conversaciones",
        showEntries: "Mostrar entradas",
        
        // Onboarding
        welcomeToSmartGTD: "¡Bienvenido a SmartGTD!",
        connectMessagingPlatform: "Conectemos tu plataforma de mensajería para comenzar con la gestión de tareas con IA.",
        useWhatsApp: "Usa WhatsApp para gestionar tus tareas",
        useTelegram: "Usa Telegram para gestionar tus tareas",
        setupLater: "Configurar Después",
        continue: "Continuar",
        enterPhoneNumber: "Ingresa Tu Número de Teléfono",
        enterUsername: "Ingresa Tu Nombre de Usuario",
        sendVerificationMessage: "Te enviaremos un mensaje para verificar tu cuenta de {platform}.",
        phoneNumberPlaceholder: "612 345 678",
        usernamePlaceholder: "@usuario",
        sendVerification: "Enviar Verificación",
        sending: "Enviando...",
        checkYourMessages: "Revisa Tus Mensajes",
        verificationSent: "Hemos enviado un mensaje de verificación a tu {platform}. Ingresa el código a continuación.",
        enterVerificationCode: "Ingresa el código de verificación",
        verifying: "Verificando...",
        verifyComplete: "Verificar y Completar",
        allSet: "¡Todo Listo! 🎉",
        platformConnected: "Tu {platform} está conectado. ¡Puedes comenzar a gestionar tus tareas con asistencia de IA!",
        nextSteps: "Próximos pasos:",
        nextStepsMessage: "Envía un mensaje como 'Agregar comprar víveres a mi lista de compras' para comenzar!",
        
        // Verification Messages
        whatsappVerificationSuccess: "✅ ¡Verificación exitosa! Puedes comenzar a chatear con SmartGTD. Prueba preguntando: '¿Qué puedes hacer?'",
        whatsappWelcomeMessage: "🎉 ¡Bienvenido a SmartGTD! Soy tu asistente de productividad con IA. Puedo ayudarte a gestionar tareas, recordatorios y tu agenda usando la metodología GTD. Para comenzar, prueba enviarme un mensaje como: 'Agregar comprar víveres a mi lista de compras' o 'Configurar recordatorio para llamar al médico mañana a las 2pm'. ¡Escribe 'ayuda' para ver más comandos!",
        
        // Common
        loading: "Cargando...",
        error: "Error",
        retry: "Reintentar",
        cancel: "Cancelar",
        save: "Guardar",
        delete: "Eliminar",
        edit: "Editar",
        close: "Cerrar",
        back: "Atrás",
        next: "Siguiente",
        previous: "Anterior",
        
        // Pricing & Subscription
        pricing: {
            title: "Elige Tu Plan",
            subtitle: "Comienza tu prueba gratuita de 3 días - Sin tarjeta de crédito",
            monthly: "Mensual",
            annual: "Anual",
            perMonth: "/mes",
            perYear: "/año",
            save: "Ahorra",
            freeTrial: "Prueba gratuita de 3 días",
            feature1: "Gestión de tareas con IA",
            feature2: "Acceso multiplataforma (Telegram, WhatsApp, Web)",
            feature3: "Programación inteligente y recordatorios",
            feature4: "Tareas y conversaciones ilimitadas",
            startTrial: "Iniciar Prueba Gratuita de 3 Días",
            trialInfo: "3 días de prueba gratuita • Sin tarjeta de crédito",
            alreadyHaveAccount: "¿Ya tienes una cuenta?",
            signIn: "Iniciar Sesión"
        },
        
        trial: {
            daysRemaining: "{days} días restantes en tu prueba",
            endsToday: "¡La prueba termina hoy!",
            addPayment: "Agregar Método de Pago"
        },
        
        payment: {
            title: "Agregar Método de Pago",
            trialRemaining: "{days} días restantes en tu prueba",
            selectedPlan: "Plan Seleccionado",
            amount: "Monto",
            benefit1: "Continúa usando SmartGTD después de la prueba",
            benefit2: "Cancela cuando quieras, sin preguntas",
            benefit3: "Pago seguro con Stripe",
            continueBtn: "Continuar al Pago",
            redirecting: "Redirigiendo a Stripe...",
            securePayment: "🔒 Pago seguro con Stripe",
            error: "Error al crear sesión de pago",
            noSubscription: "No se encontró suscripción",
            selectPlan: "Seleccionar un Plan"
        },
        
        auth: {
            signUp: "Registrarse",
            signIn: "Iniciar Sesión",
            createAccount: "Crea tu cuenta",
            welcomeBack: "Bienvenido de nuevo",
            confirmEmail: "Confirma tu correo electrónico",
            name: "Nombre",
            firstName: "Nombre",
            lastName: "Apellido",
            username: "Nombre de usuario",
            phone: "Teléfono",
            email: "Correo electrónico",
            password: "Contraseña",
            processing: "Procesando...",
            or: "O",
            continueWithGoogle: "Continuar con Google",
            alreadyHaveAccount: "¿Ya tienes una cuenta? Iniciar Sesión",
            dontHaveAccount: "¿No tienes una cuenta? Registrarse",
            backToPricing: "← Volver a planes",
            verificationSent: "Hemos enviado un código de verificación a {email}. Por favor, revisa tu correo e ingresa el código a continuación.",
            verificationCode: "Código de Verificación",
            resendCode: "Reenviar Código",
            codeResent: "¡Código de verificación reenviado! Revisa tu correo.",
            emailConfirmed: "¡Correo confirmado! Ahora puedes iniciar sesión."
        },
        
        onboarding: {
            trialBadge: "🎉 2 Días Gratis",
            stepPlan: "Plan",
            stepAccount: "Cuenta",
            stepSetup: "Configurar"
        },
        
        subscription: {
            tab: "Suscripción",
            tabDesc: "Gestiona tu suscripción y facturación",
            title: "Gestión de Suscripción",
            currentPlan: "Plan Actual",
            statusActive: "Activo",
            statusTrial: "Prueba",
            statusExpired: "Expirado",
            statusCanceled: "Cancelado",
            trialRemaining: "{days} días restantes en la prueba",
            addPaymentPrompt: "Agrega método de pago para continuar después de la prueba",
            planDetails: "Detalles del Plan",
            planType: "Tipo de Plan",
            currency: "Moneda",
            paymentMethod: "Método de Pago",
            added: "Agregado",
            notAdded: "No Agregado",
            manageButton: "Gestionar Suscripción",
            fetchError: "Error al cargar suscripción",
            portalError: "Error al abrir portal de facturación",
            infoTitle: "Información de Suscripción:",
            info1: "Gestiona métodos de pago y detalles de facturación",
            info2: "Ver facturas e historial de pagos",
            info3: "Cancela o actualiza tu suscripción en cualquier momento",
            trialEnding: "Prueba Terminando Pronto",
            trialActive: "Tu prueba está activa",
            upgradeToPro: "Actualizar a Pro",
            subscriptionInactive: "Suscripción Inactiva",
            reactivateMessage: "Tu suscripción no está activa. Reactiva para continuar usando SmartGTD.",
            accessLocked: "Acceso Bloqueado",
            reactivateToAccess: "Tu suscripción fue cancelada. Reactiva para recuperar el acceso a todas las funciones.",
            expiredMessage: "Tu suscripción ha expirado. Suscríbete para continuar usando SmartGTD.",
            subscribeToAccess: "Suscríbete para acceder a las funciones de SmartGTD.",
            updatePayment: "Actualizar Método de Pago",
            subscriptionRequired: "Suscripción Requerida",
            usageLimitExceeded: "Límite Mensual de Uso Excedido",
            activateToAccess: "Activa tu suscripción para acceder a todas las funciones y datos.",
            noSubscription: "Sin Suscripción Activa",
            noSubscriptionDesc: "Aún no tienes una suscripción activa. ¡Elige un plan para comenzar!",
            viewPlans: "Ver Planes",
            manageSubscription: "Gestionar tu suscripción",
            creditsUsage: "Uso de Créditos",
            credits: "créditos",
            creditsCount: "{used} / {limit} créditos",
            inputCredits: "Entrada",
            outputCredits: "Salida",
            creditsWarning: "Te estás acercando a tu límite mensual de créditos. Considera actualizar tu plan.",
            creditsLimitReached: "Has alcanzado tu límite mensual de créditos. Por favor, actualiza tu plan o espera hasta el próximo mes."
        },
        whatsapp: {
            setupTitle: "Conectar WhatsApp",
            setupDescription: "Recibe actualizaciones instantáneas de tareas y gestiona tu flujo GTD vía WhatsApp",
            setupButton: "Configurar WhatsApp"
        }
    }
};

// Get browser language or default to English
function getBrowserLanguage() {
    const browserLang = navigator.language.split('-')[0];
    const supportedLangs = ['en', 'pt', 'es'];
    return supportedLangs.includes(browserLang) ? browserLang : 'en';
}

// Translation hook
export function useTranslation() {
    // Try to get language from URL params first (for cross-origin navigation)
    const getInitialLanguage = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang = urlParams.get('lang');
        if (urlLang) {
            const langCode = urlLang.split('-')[0];
            if (['en', 'pt', 'es'].includes(langCode)) {
                localStorage.setItem('smartgtd-language', urlLang);
                return langCode;
            }
        }
        const stored = localStorage.getItem('smartgtd-language');
        return stored ? stored.split('-')[0] : getBrowserLanguage();
    };
    
    const [currentLang, setCurrentLang] = React.useState(getInitialLanguage());
    
    React.useEffect(() => {
        // Listen for language changes
        const handleStorageChange = () => {
            const stored = localStorage.getItem('smartgtd-language');
            const newLang = stored ? stored.split('-')[0] : getBrowserLanguage();
            setCurrentLang(newLang);
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);
    
    const t = (key, params = {}) => {
        const keys = key.split('.');
        let value = translations[currentLang];
        
        for (const k of keys) {
            value = value?.[k];
        }
        
        if (!value) return key;
        
        // Replace parameters like {count}, {platform}, etc.
        return Object.keys(params).reduce((str, param) => {
            return str.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
        }, value);
    };
    
    const setLanguage = (lang) => {
        const langCode = lang.split('-')[0];
        if (translations[langCode]) {
            localStorage.setItem('smartgtd-language', lang);
            setCurrentLang(langCode);
        }
    };
    
    return { t, language: currentLang, currentLang, setLanguage };
}

export default translations;